import { ErrorConstants } from "../constants";
import { IAuthenticatedUser } from "../models/authenticated-user.interface";
import { Gender } from "../models/base-user.interface";
import { KeyValue } from "../models/key-value";
import { AddOrganizationUserFailureReason } from "../models/results/add-org-user-result.interface";
import Role from "../models/role";
import {
  BeltSize,
  UserDetailsFormInputs,
  UserDetailsFormPermissions,
  UserStatus,
  UserTitle,
} from "../models/users.interface";
import { getRoleFromServerRoleAndOrg } from "./authorizationUtil";

export const getFullNameWithTitle = (
  title?: string,
  firstName?: string,
  lastName?: string
) => {
  return `${title ?? ""} ${firstName ?? ""} ${lastName ?? ""}`.trim();
};

export const getGenderFromServerGender = (gender?: number | string): Gender => {
  switch (gender) {
    case 1:
    case "Male":
      return "Male";
    case 2:
    case "Female":
    default:
      return "Female";
    // default:
    //   return "Other";
  }
};

export const getServerGenderFromGender = (gender?: Gender): 1 | 2 | 3 => {
  switch (gender) {
    case "Male":
      return 1;
    case "Female":
      return 2;
    default:
      return 3;
  }
};

export const getBeltSizeFromServerBeltSize = (beltSize?: number): BeltSize => {
  switch (beltSize) {
    case 2:
      return "M";
    case 3:
      return "L";
    case 4:
      return "XL";
    default:
      return "S";
  }
};

export const getServerBeltSizeFromBeltSize = (
  beltSize?: BeltSize
): 1 | 2 | 3 | 4 => {
  switch (beltSize) {
    case "M":
      return 2;
    case "L":
      return 3;
    case "XL":
      return 4;
    default:
      return 1;
  }
};

export const getStatusFromServerStatus = (status: number): UserStatus => {
  switch (status) {
    case 0:
      return "active";
    case 2:
      return "pending";
    default:
      return "disabled";
  }
};

export const getUserTitleFromServerTitle = (title: string): UserTitle => {
  const titleLowerCase = title?.toLowerCase();
  switch (titleLowerCase) {
    case "dr":
    case "dr.":
      return UserTitle.Dr;
    case "prof":
    case "prof.":
      return UserTitle.Prof;
    default:
      return UserTitle.None;
  }
};

export const getProUserDefaultFormPermissions = (
  type: "pro" | "admin",
  opts?: {
    isCroOrg?: boolean;
    isCroExternOrg?: boolean;
  }
): UserDetailsFormPermissions => {
  const isAdmin = type === "admin";

  const permissions: UserDetailsFormPermissions = {
    isAdmin: isAdmin,
    canManageProUsers: isAdmin,
    canLogIntoPortal: true,
    canEditInterpretation: false,
    canManageNonProUsers: false,
    canViewEcgs: !isAdmin,
    canViewReports: isAdmin,
    canSendEcgsViaMail: false,
    canViewSelfClinicsEcgs: false,
    canArchiveEcgs: false,
  };

  if (opts?.isCroOrg || opts?.isCroExternOrg) {
    addCroDefaultPermissions(permissions);
  }

  return permissions;
};

export const getAddOrganizationUserFailure = (
  errorCode: string,
  userCode: number
): AddOrganizationUserFailureReason => {
  if (errorCode !== ErrorConstants.CREATE_USER_WITH_EMAIL_ALREADY_IN_USE_CODE)
    return AddOrganizationUserFailureReason.Unknown;

  const userRole = getRoleFromServerRoleAndOrg(userCode);
  switch (userRole) {
    case Role.OrgNonProfessional:
      return AddOrganizationUserFailureReason.PatientAccountExists;
    case Role.OrgProfessional:
    case Role.OrgAdmin:
      return AddOrganizationUserFailureReason.ProUserExists;
    default:
      return AddOrganizationUserFailureReason.Unknown;
  }
};

const addCroDefaultPermissions = (
  permissions: Partial<UserDetailsFormInputs>
) => {
  permissions.canApproveEcg = false;
  return permissions;
};

export const isAdmin = (user: IAuthenticatedUser) => user.role === 4;

export const isEmailDomainsExist = (items: KeyValue<string>[] | null) =>
  items && items.length !== 0;
